import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { NotificationsProvider } from './lib/notifications';
import { MessagingProvider } from './lib/messaging';
import { OrgProvider } from './lib/org';
import { SiteSettingsProvider, SiteSettingsOrgBridge, useSiteSettings } from './lib/siteSettings';
import { useOrg } from './lib/org';
import { orgTierHasFeature } from './lib/tierFeatures';
import { FEATURE_NAMES } from './lib/upgrade';
import UpgradeNotice from './components/UpgradeNotice';
import Layout from './components/Layout';
import InvitePeople from './pages/InvitePeople';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import VerifySecondaryEmail from './pages/VerifySecondaryEmail';
import OAuthCallback from './pages/OAuthCallback';
import Dashboard from './pages/Dashboard';
import ProofOfFunds from './pages/ProofOfFunds';
import ReviewQueue from './pages/ReviewQueue';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Files from './pages/Files';
import Forum from './pages/Forum';
import ForumCategory from './pages/ForumCategory';
import ForumThread from './pages/ForumThread';
import ForumSearch from './pages/ForumSearch';
import Notifications from './pages/Notifications';
import Tasks from './pages/Tasks';
import Schedules from './pages/Schedules';
import Profile from './pages/Profile';
import Ribbons from './pages/Ribbons';
import MemberProfile from './pages/MemberProfile';
import Admin from './pages/Admin';
import Groups from './pages/Groups';
import GroupDetail from './pages/GroupDetail';
import OrgSettings from './pages/OrgSettings';
import OrgDirectory from './pages/OrgDirectory';
import OrgDetail from './pages/OrgDetail';
import CreateOrg from './pages/CreateOrg';
import PlatformAdmin from './pages/PlatformAdmin';
import OrgSetupWizard from './pages/OrgSetupWizard';
import OrgLanding from './pages/OrgLanding';
import PublicEvents from './pages/PublicEvents';
import PublicEventDetail from './pages/PublicEventDetail';
import PublicVideos from './pages/PublicVideos';
import OrgLogin from './pages/OrgLogin';
import OrgRegister from './pages/OrgRegister';
import Videos from './pages/Videos';
import VideoDetail from './pages/VideoDetail';
import Playlists from './pages/Playlists';
import Messages from './pages/Messages';
import ConversationDetail from './pages/ConversationDetail';
import AcceptInvite from './pages/AcceptInvite';
import NotFound from './pages/NotFound';
import Donate, { DonateResult } from './pages/Donate';
import OrgPayments from './pages/OrgPayments';
import OrgDonations from './pages/OrgDonations';
import SiteBuilder from './pages/SiteBuilder';
import PublicSite from './pages/PublicSite';
import MemberServices from './pages/MemberServices';
import Marketplace from './pages/Marketplace';
import VendorPage from './pages/VendorPage';
import VendorWorkspace from './pages/VendorWorkspace';
import OrgMarketplace from './pages/OrgMarketplace';
import MarketplaceTerms from './pages/MarketplaceTerms';
import MarketplaceFollowUp from './pages/MarketplaceFollowUp';

/**
 * FEA-30 (docs/FRONTEND-PLAN.md §4.3): a feature-toggled-off surface
 * redirects home instead of rendering. Admins ALWAYS get `children` here —
 * regardless of the toggle — so an admin can navigate into a surface they
 * just switched off to judge the effect, rather than being locked out of the
 * very page the Settings tab's toggle controls. (Layout's nav filter carries
 * the same admin exemption, so the nav item and the route agree.)
 */
function OrgSettingsBridge() {
  const { activeOrg } = useOrg();
  return <SiteSettingsOrgBridge orgId={activeOrg?.orgId} />;
}

function FeatureRoute({ feature, children }) {
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const { activeOrg } = useOrg();
  if (user?.role === 'admin' || user?.platformRole === 'platform-admin') return children;
  if (settings.features[feature] === false) return <Navigate to="/" replace />;
  // A feature the plan does not include stays reachable and says how to get
  // it (docs/Decisions.md D52 item 4), instead of bouncing to the dashboard.
  if (activeOrg && !orgTierHasFeature(activeOrg.tier, feature)) {
    return (
      <UpgradeNotice
        what={FEATURE_NAMES[feature] || 'This feature'}
        isOrgAdmin={activeOrg.role === 'org-admin'}
      />
    );
  }
  return children;
}

export default function App() {
  return (
    // Mounted above the auth split so public pages (Login/Register) can read
    // it too — Register needs registrationOpen before anyone signs in.
    <SiteSettingsProvider>
      <AuthProvider>
        <OrgProvider>
          <OrgSettingsBridge />
          <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              {/* Public: the recipient of a secondary-email link may not be signed in. */}
              <Route path="/verify-secondary-email" element={<VerifySecondaryEmail />} />
              <Route path="/auth/oauth/callback/:provider" element={<OAuthCallback />} />

              {/* Public org pages (MTN-04) */}
              <Route path="/o/:orgSlug" element={<OrgLanding />} />
              <Route path="/o/:orgSlug/events" element={<PublicEvents />} />
              <Route path="/o/:orgSlug/events/:eventId" element={<PublicEventDetail />} />
              <Route path="/o/:orgSlug/videos" element={<PublicVideos />} />
              <Route path="/o/:orgSlug/login" element={<OrgLogin />} />
              <Route path="/o/:orgSlug/register" element={<OrgRegister />} />
              <Route path="/o/:orgSlug/invite" element={<AcceptInvite />} />
              <Route path="/o/:orgSlug/donate" element={<Donate />} />
              <Route path="/o/:orgSlug/donate/success" element={<DonateResult status="success" />} />
              <Route path="/o/:orgSlug/donate/cancel" element={<DonateResult status="cancel" />} />
              {/* Public organization websites (docs/Decisions.md D43) */}
              <Route path="/s/:slug/:page?" element={<PublicSite />} />

              {/*
              Authenticated app shell. NotificationsProvider sits inside
              ProtectedRoute (so the unread poll never runs on the login screens)
              and outside Layout (so the nav badge can read it).
            */}
              <Route
                element={
                  <ProtectedRoute>
                    <NotificationsProvider>
                      <MessagingProvider>
                        <Layout />
                      </MessagingProvider>
                    </NotificationsProvider>
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/proof-of-funds" element={<ProofOfFunds />} />
                <Route
                  path="/review"
                  element={
                    <ProtectedRoute reviewerOnly>
                      <ReviewQueue />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/events"
                  element={
                    <FeatureRoute feature="events">
                      <Events />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/events/:eventId"
                  element={
                    <FeatureRoute feature="events">
                      <EventDetail />
                    </FeatureRoute>
                  }
                />
                <Route path="/files" element={<Files />} />
                <Route
                  path="/videos"
                  element={
                    <FeatureRoute feature="videos">
                      <Videos />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/videos/:videoId"
                  element={
                    <FeatureRoute feature="videos">
                      <VideoDetail />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/playlists"
                  element={
                    <FeatureRoute feature="videos">
                      <Playlists />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <FeatureRoute feature="inbox">
                      <Messages />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/messages/:conversationId"
                  element={
                    <FeatureRoute feature="inbox">
                      <ConversationDetail />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/forum"
                  element={
                    <FeatureRoute feature="forum">
                      <Forum />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/forum/search"
                  element={
                    <FeatureRoute feature="forum">
                      <ForumSearch />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/forum/categories/:categoryId"
                  element={
                    <FeatureRoute feature="forum">
                      <ForumCategory />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/forum/threads/:threadId"
                  element={
                    <FeatureRoute feature="forum">
                      <ForumThread />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/groups"
                  element={
                    <FeatureRoute feature="groups">
                      <Groups />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/groups/:groupId"
                  element={
                    <FeatureRoute feature="groups">
                      <GroupDetail />
                    </FeatureRoute>
                  }
                />
                <Route path="/notifications" element={<Notifications />} />
                <Route
                  path="/tasks"
                  element={
                    <FeatureRoute feature="tasks">
                      <Tasks />
                    </FeatureRoute>
                  }
                />
                <Route
                  path="/schedules"
                  element={
                    <FeatureRoute feature="calendar">
                      <Schedules />
                    </FeatureRoute>
                  }
                />
                <Route path="/profile" element={<Profile />} />
                <Route path="/ribbons" element={<Ribbons />} />
                <Route path="/members/:userId" element={<MemberProfile />} />
                <Route path="/org/settings" element={<OrgSettings />} />
                <Route path="/org/payments" element={<OrgPayments />} />
                <Route path="/org/donations" element={<OrgDonations />} />
                <Route path="/org/site" element={<SiteBuilder />} />
                <Route path="/org/setup" element={<OrgSetupWizard />} />
                <Route path="/org/invite" element={<InvitePeople />} />
                <Route path="/services" element={<MemberServices />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/marketplace/terms" element={<MarketplaceTerms />} />
                <Route path="/marketplace/follow-up/:leadId" element={<MarketplaceFollowUp />} />
                <Route path="/marketplace/vendors/:vendorId" element={<VendorPage />} />
                <Route path="/vendor" element={<VendorWorkspace />} />
                <Route path="/org/marketplace" element={<OrgMarketplace />} />
                <Route path="/orgs" element={<OrgDirectory />} />
                <Route path="/orgs/new" element={<CreateOrg />} />
                <Route path="/orgs/:orgId" element={<OrgDetail />} />
                <Route path="/platform/admin" element={<PlatformAdmin />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </OrgProvider>
      </AuthProvider>
    </SiteSettingsProvider>
  );
}
